import type {
    ActiveDraft,
    ImportHistoryEntry,
    PendingInvoice,
    PurchaseInvoicePage,
    PurchaseSummary,
    ReceivedInvoice,
} from '@/api/models';
import { tetriFromDecimal } from '@/utils/money';

/**
 * Wire → domain for purchase money (BOS-162).
 *
 * The purchase endpoints send decimal GEL. Every amount gains a `*Tetri` twin
 * here, at the one edge that crosses, so the three purchases tables format and
 * compare integers. The decimal fields stay as the wire's echo; nothing
 * downstream should compute on them.
 *
 * `tetriFromDecimal` parses digit positions rather than multiplying by 100, so
 * an invoice total cannot drift by a tetri between the row and the ledger.
 */

const tetriOfNullable = (value: number | null): number | null =>
    value === null ? null : tetriFromDecimal(value);

/** The twins every invoice shape carries, pending or received. */
const pendingTwins = (wire: PendingInvoice) => ({
    expensesTotalTetri: tetriFromDecimal(wire.expensesTotal),
    linesTotalTetri: tetriFromDecimal(wire.linesTotal),
    totalAmountTetri: tetriFromDecimal(wire.totalAmount),
});

/** The extra twins a received invoice carries once it has posted. */
const receivedTwins = (wire: ReceivedInvoice) => ({
    ...pendingTwins(wire),
    vatTotalTetri: tetriFromDecimal(wire.vatTotal),
    grossTotalTetri: tetriFromDecimal(wire.grossTotal),
    expensesOnInvoiceTotalTetri: tetriFromDecimal(wire.expensesOnInvoiceTotal),
    payableTotalTetri: tetriFromDecimal(wire.payableTotal),
    paidAmountTetri: tetriFromDecimal(wire.paidAmount),
});

export const toPendingInvoice = (wire: PendingInvoice): PendingInvoice => ({
    ...wire,
    ...pendingTwins(wire),
});

export const toReceivedInvoice = (wire: ReceivedInvoice): ReceivedInvoice => ({
    ...wire,
    ...receivedTwins(wire),
});

export const toImportHistoryEntry = (
    wire: ImportHistoryEntry,
): ImportHistoryEntry => ({
    ...wire,
    ...receivedTwins(wire),
});

/**
 * A draft's figures are nullable — nothing has been entered yet — and stay
 * nullable in tetri, because `0` is a real amount and "not typed" is not.
 */
export const toActiveDraft = (wire: ActiveDraft): ActiveDraft => ({
    ...wire,
    expensesTotalTetri: tetriOfNullable(wire.expensesTotal),
    linesTotalTetri: tetriOfNullable(wire.linesTotal),
    totalAmountTetri: tetriOfNullable(wire.totalAmount),
});

export const toPurchaseSummary = (wire: PurchaseSummary): PurchaseSummary => ({
    ...wire,
    totalPurchasesTetri: tetriFromDecimal(wire.totalPurchases),
});

export const toPage = <TItem>(
    wire: PurchaseInvoicePage<TItem>,
    map: (item: TItem) => TItem,
): PurchaseInvoicePage<TItem> => ({
    ...wire,
    items: (wire.items ?? []).map(map),
});
